/**
 * 送金依頼（transfer_requests）データモデル（v3.x / 2026-07）。
 *
 * 海外メーカー仕入フローの「03 送金依頼 → 04 送金完了」を管理する。
 * トップレベルコレクション `transfer_requests`（1 件 = 1 発注商品の送金依頼）。
 * 経理画面が deal/contact/category を join せず一覧表示できるよう、発行時点の
 * スナップショットを非正規化保存する（報酬台帳 / SA請求書と同じ設計思想）。
 *
 * 状態遷移: requested（経理タブに表示）→ completed（送金完了・一覧から外れる）
 *           / cancelled（取消）
 * 書込は Callable（addTransferRequest / sendTransferRequestsToAccounting /
 * completeTransferRequests。旧 submitTransferRequest）のみ。
 */
import type { IsoOrTimestamp } from './common';

export type TransferRequestStatus = 'requested' | 'completed' | 'cancelled';

export interface TransferRequest {
  // ── 紐付け（元データ参照用）──
  dealId: string;
  poId: string | null;
  manufacturerQuoteId: string | null;
  contactId: string | null;

  // ── カテゴリー（タブのグルーピング + メーカーメール解決）──
  categoryId: string | null;
  categoryName: string | null;

  // ── 表示用スナップショット（発行時点で凍結）──
  /** 顧客名（日本語）。 */
  customerName: string;
  /** 顧客名（英語）。送金完了メールで使用。 */
  customerNameEn: string | null;
  /** 発注商品名 / 案件名。 */
  productName: string;
  invoiceNumber: string | null;

  /** 通貨コード（TimeWaver は 'EUR'）。 */
  currency: string;
  totalAmount: number;
  discountAmount: number;
  /** 送金額 = totalAmount - discountAmount。 */
  finalAmount: number;

  // ── 状態 ──
  status: TransferRequestStatus;
  requestedAt: IsoOrTimestamp;
  requestedBy: string;
  memo?: string | null;
  /** 経理への依頼メール送信時刻 (2026-10-26 以前の「編集して送信」画面で送ったもの)。 */
  accountingMailSentAt?: IsoOrTimestamp | null;
  /**
   * 「送金依頼を送信」で経理へ連絡した最初の時刻 (2026-10-26 追加)。
   * これか accountingMailSentAt があれば、経理 > 送金依頼 に「送金依頼済み」ラベルを出す。
   */
  sentToAccountingAt?: IsoOrTimestamp | null;

  // ── 完了（メーカーへ送金完了報告）──
  completedAt?: IsoOrTimestamp | null;
  completedBy?: string | null;
  makerMailSentAt?: IsoOrTimestamp | null;
  /** 送金証憑画像の Storage パス。 */
  proofImagePath?: string | null;

  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/** 「送金依頼済み」ラベルを出すか (経理へ連絡済みか)。 */
export function isTransferSentToAccounting(
  r: Pick<TransferRequest, 'sentToAccountingAt' | 'accountingMailSentAt'>,
): boolean {
  return !!(r.sentToAccountingAt || r.accountingMailSentAt);
}
